import { gsap } from "gsap";
import Lenis from '@studio-freight/lenis'
import SplitType from 'split-type'
import {
	Scene,
	OrthographicCamera,
	WebGLRenderer,
	Vector2,
	TextureLoader,
	RepeatWrapping,
	NearestFilter,
	NoColorSpace,
	ShaderMaterial,
	PlaneGeometry,
	Mesh,
	Color,
} from 'three'

// Split specific text elements for precise animation control
const mainHeading = new SplitType('h1', { types: 'words, chars' }) // Carl Barrett
const sectionHeadings = new SplitType('section:nth-child(2) h2, section:nth-child(3) h2', { types: 'words, chars' }) // Currently, Previously
const recognitionHeading = new SplitType('section:nth-child(4) h2', { types: 'words, chars' }) // Recognition
const missionText = new SplitType('#mission', { types: 'words, chars' }) // Description paragraph
const portfolioLinks = new SplitType('section:nth-child(1) ul a', { types: 'words, chars' }) // Portfolio links (first section only)
const portfolioLabels = new SplitType('section:nth-child(1) ul li h4', { types: 'words, chars' }) // Portfolio labels (Portfolio, Email, etc.)
const currentCompany = new SplitType('section:nth-child(2) article header h3', { types: 'words, chars' }) // Veeva Systems
const previousCompanies = new SplitType('section:nth-child(3) ul li h3', { types: 'words, chars' }) // Previous company names
const recognitionLinks = new SplitType('section:nth-child(4) li header a', { types: 'words, chars' }) // Recognition links (MotorTrend, etc.)
const recognitionTitles = new SplitType('section:nth-child(4) li header h4', { types: 'words, chars' }) // Recognition titles (2021, SUV of the Year, etc.)

// Set initial state to prevent flash
gsap.set('#mission, a, h1, h2, h3, h4, p', {
	visibility: 'visible'
})

// Set initial animation state for all elements
const allAnimatedElements = [
	...mainHeading.chars,
	...sectionHeadings.chars,
	...recognitionHeading.chars,
	...missionText.chars,
	...portfolioLinks.chars,
	...portfolioLabels.chars,
	...currentCompany.chars,
	...previousCompanies.chars,
	...recognitionLinks.chars,
	...recognitionTitles.chars
]

gsap.set(allAnimatedElements, {
	opacity: 0,
	y: 25,
	filter: 'blur(10px)'
})

const charTween = {
	opacity: 1,
	y: 0,
	filter: 'blur(0px)',
	duration: 0.5
}

// Create a timeline for staggered animations
const tl = gsap.timeline({
	onComplete() {
		// blur(0px) still promotes compositor layers — drop it after the intro
		gsap.set(allAnimatedElements, { clearProps: 'filter' })
	}
})

// 1. First: Carl Barrett, Currently, Previously
tl.to(mainHeading.chars, { ...charTween, stagger: { amount: 0.2 } })

tl.to(sectionHeadings.chars, { ...charTween, stagger: { amount: 0.2 } }, "-=0.1")

// 2. Second: Description paragraph
tl.to(missionText.chars, { ...charTween, stagger: { amount: 0.3 } }, "-=0.05")

// 3. Third: Portfolio links
tl.to(portfolioLinks.chars, { ...charTween, stagger: { amount: 0.1 } }, "-=0.05")

tl.to(portfolioLabels.chars, { ...charTween, stagger: { amount: 0.1 } }, "-=0.1")

// 4. Fourth: Current company (Veeva Systems)
tl.to(currentCompany.chars, { ...charTween, stagger: { amount: 0.1 } }, "-=0.05")

// 5. Fifth: Previous companies
tl.to(previousCompanies.chars, { ...charTween, stagger: { amount: 0.1 } }, "-=0.05")

// 6. Sixth: Recognition heading
tl.to(recognitionHeading.chars, { ...charTween, stagger: { amount: 0.2 } }, "-=0.05")

// 7. Seventh: Recognition links (MotorTrend, SBDAutomotive, etc.)
tl.to(recognitionLinks.chars, { ...charTween, stagger: { amount: 0.1 } }, "-=0.05")

// 8. Eighth: Recognition titles (2021, SUV of the Year, etc.)
tl.to(recognitionTitles.chars, { ...charTween, stagger: { amount: 0.1 } }, "-=0.1")

const lenis = new Lenis({
	duration: 1.2,
	easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
})

// Line-by-line scroll-based blur effect with overflow
const blurElements = Array.from(document.querySelectorAll('h1, h2, h3, h4, p, a, li'))
const lastBlur = new Float32Array(blurElements.length)
const nextBlur = new Float32Array(blurElements.length)
let blurDirty = true

function handleLineByLineBlur() {
	if (!blurElements.length) return

	const windowHeight = window.innerHeight
	const blurStartPoint = windowHeight * 0.15
	const blurEndPoint = -windowHeight * 0.1
	const maxBlurDistance = blurStartPoint - blurEndPoint

	for (let i = 0; i < blurElements.length; i++) {
		const rect = blurElements[i].getBoundingClientRect()
		const distanceFromTop = rect.top + (rect.height / 2)

		let blurAmount = 0
		if (distanceFromTop < blurStartPoint && distanceFromTop > blurEndPoint) {
			blurAmount = Math.min((blurStartPoint - distanceFromTop) / maxBlurDistance * 15, 15)
		} else if (distanceFromTop <= blurEndPoint) {
			blurAmount = 15
		}
		nextBlur[i] = blurAmount
	}

	for (let i = 0; i < blurElements.length; i++) {
		const blurAmount = nextBlur[i]
		if (Math.abs(lastBlur[i] - blurAmount) < 0.05) continue
		lastBlur[i] = blurAmount
		// 'none' lets the browser drop the filter layer; blur(0px) does not
		blurElements[i].style.filter = blurAmount > 0.05 ? `blur(${blurAmount}px)` : 'none'
	}
}

lenis.on('scroll', () => { blurDirty = true })
handleLineByLineBlur()

// WebGL Scene Setup
const canvas = document.getElementById('webgl-canvas')
let renderWebGL = () => {}

if (!canvas) {
	console.error('WebGL canvas not found!')
} else {
	const scene = new Scene()
	const camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1)
	const renderer = new WebGLRenderer({
		canvas,
		alpha: false,
		antialias: false,
		depth: false,
		stencil: false,
		powerPreference: 'high-performance',
	})
	renderer.setClearColor(new Color(0x1d1d1d), 1)
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
	renderer.setSize(window.innerWidth, window.innerHeight, false)

	const mouse = new Vector2()
	const mouseTarget = new Vector2()

	const textureLoader = new TextureLoader()
	const rockNormal = textureLoader.load('./dist/textures/rock_face_03_nor_gl_1k_compressed.jpg')
	rockNormal.wrapS = RepeatWrapping
	rockNormal.wrapT = RepeatWrapping

	const grainTexture = textureLoader.load('./dist/textures/grain_256.png')
	grainTexture.wrapS = RepeatWrapping
	grainTexture.wrapT = RepeatWrapping
	grainTexture.minFilter = NearestFilter
	grainTexture.magFilter = NearestFilter
	grainTexture.generateMipmaps = false
	grainTexture.colorSpace = NoColorSpace

	const maskMaterial = new ShaderMaterial({
		depthTest: false,
		depthWrite: false,
		uniforms: {
			uTime: { value: 0 },
			uMouse: { value: new Vector2(0, 0) },
			uResolution: { value: new Vector2(window.innerWidth, window.innerHeight) },
			uRockNormal: { value: rockNormal },
			uGrain: { value: grainTexture }
		},
		vertexShader: `
			varying vec2 vUv;
			void main() {
				vUv = uv;
				gl_Position = vec4(position, 1.0);
			}
		`,
		fragmentShader: `
			uniform float uTime;
			uniform vec2 uMouse;
			uniform vec2 uResolution;
			uniform sampler2D uRockNormal;
			uniform sampler2D uGrain;
			varying vec2 vUv;
			
			// Get normal from normal map texture
			vec3 getNormalFromMap(vec2 uv) {
				// Calculate aspect ratio to prevent stretching
				float aspect = uResolution.x / uResolution.y;
				
				// Apply fixed tiling scale that compensates for aspect ratio
				vec2 tiledUV = uv;
				if (aspect > 1.0) {
					// Wider screen - compensate X to prevent stretching
					tiledUV.x *= aspect;
				} else {
					// Taller screen - compensate Y to prevent stretching
					tiledUV.y /= aspect;
				}
				tiledUV *= 1.25; // Fixed tiling scale
				
				// Sample the normal map with aspect-compensated UVs
				vec3 normalMap = texture2D(uRockNormal, tiledUV).rgb;
				// Convert from [0,1] to [-1,1] range
				normalMap = normalMap * 2.0 - 1.0;
				// Return the normal (normal maps are already in world space)
				return normalize(normalMap);
			}
			
			vec3 addFilmGrain(vec3 color, float time) {
				// ~2.5px cells ≈ old uv*800 at 1920. Jump offset each frame so it sparkles, not crawls.
				vec2 grainUV = gl_FragCoord.xy / 2.5;
				float frame = floor(time * 24.0);
				grainUV += vec2(frame * 19.0, frame * 27.0);
				float grain = texture2D(uGrain, grainUV / 256.0).r;
				grain = grain * 0.015 + 0.008;
				return color + vec3(grain);
			}
			
			void main() {
				vec2 uv = vUv;
				
				// Calculate distance from mouse position
				vec2 mousePos = uMouse / uResolution;
				mousePos.y = 1.0 - mousePos.y; // Flip Y coordinate
				
				// Adjust for aspect ratio to make perfect circle
				float aspect = uResolution.x / uResolution.y;
				vec2 adjustedUV = uv;
				adjustedUV.x *= aspect;
				vec2 adjustedMousePos = mousePos;
				adjustedMousePos.x *= aspect;
				
				float dist = distance(adjustedUV, adjustedMousePos);
				float radius = min(0.25 * aspect, 0.35); // Size of the reveal circle with max limit
				float smoothness = min(0.12 * aspect, 0.15); // Softer edge with max limit
				
				// Get normal map to create organic mask shape
				vec3 baseNormal = getNormalFromMap(uv);
				
				// Create organic mask that dramatically follows the normal map
				float normalInfluence = (baseNormal.z + 1.0) * 0.5; // Convert normal Z to 0-1 range
				normalInfluence = pow(normalInfluence, 0.3); // Even more dramatic normal influence
				float distanceFalloff = 1.0 - smoothstep(0.0, 0.7, dist); // Larger falloff area
				float organicMask = normalInfluence * distanceFalloff; // Normal map is zero outside falloff area
				
				// Create smaller radial falloff - stronger near cursor, weaker far away
				float radialFalloff = 1.0 - smoothstep(0.0, 0.3, dist); // Smaller, more focused falloff
				radialFalloff = pow(radialFalloff, 2.0); // Sharper falloff curve
				
				// Combine organic mask with subtle radial falloff
				float heightAnimation = organicMask * (0.5 + radialFalloff * 0.5); // Reduce radial influence
				
				// Add smooth easing to the final height animation
				heightAnimation = smoothstep(0.0, 1.0, heightAnimation); // Smooth transition
				heightAnimation = pow(heightAnimation, 1.2); // Gentle ease-out curve
				
				// Use the baseNormal we already calculated above
				
				// Create cursor-focused displacement enhancement
				float cursorDistance = dist / (radius * aspect); // Distance from cursor center (0 to 1)
				float cursorDisplacement = 1.0 - smoothstep(0.0, 0.4, cursorDistance); // Cursor displacement area
				cursorDisplacement = pow(cursorDisplacement, 1.5); // Smooth falloff
				
				// Increase normal strength with cursor enhancement
				float displacementMultiplier = 3.0 + cursorDisplacement * 2.5; // Base 3.0 + up to 2.5 more at cursor
				baseNormal.xy *= displacementMultiplier;
				baseNormal = normalize(baseNormal);
				
				// Mix between flat and dramatic normal based on animation
				vec3 normal = mix(vec3(0.0, 0.0, 1.0), baseNormal, heightAnimation);
				normal = normalize(normal);
				
				// Subtle lighting for readability
				vec3 lightDir = normalize(vec3(0.5, 0.5, 1.0));
				float diffuse = max(dot(normal, lightDir), 0.0);
				
				// Increase ambient light under cursor to prevent darkness
				float cursorAmbient = 0.04 + cursorDisplacement * 0.06; // Base 0.04 + up to 0.06 more at cursor
				float ambient = cursorAmbient;
				
				// Enhanced specular highlights
				vec3 viewDir = vec3(0.0, 0.0, 1.0);
				vec3 reflectDir = reflect(-lightDir, normal);
				float specular = pow(max(dot(viewDir, reflectDir), 0.0), 32.0);
				
				// Add cursor-focused specular highlight - brightest directly under cursor
				float cursorHighlight = 1.0 - smoothstep(0.0, 0.3, cursorDistance); // Focused area around cursor
				cursorHighlight = pow(cursorHighlight, 2.0); // Sharp falloff for focused effect
				float cursorSpecular = cursorHighlight * 1.2; // Strong cursor enhancement
				
				// Combine base specular with cursor-focused enhancement
				float enhancedSpecular = specular * (0.75 + cursorSpecular); // Base 0.75 + up to 1.2 more at cursor
				
				float lighting = diffuse * 0.8 + ambient + enhancedSpecular;
				
				// Create grayscale height-based effect
				vec3 baseColor = vec3(0.114, 0.114, 0.114); // #1D1D1D background
				
				// Create height-based grayscale variation - brighter under cursor
				float heightVariation = (baseNormal.z + 1.0) * 0.5; // Convert normal Z to 0-1 range
				float cursorBrightness = cursorDisplacement * 0.03; // Additional brightness under cursor
				vec3 heightColor = vec3(0.114 + cursorBrightness, 0.114 + cursorBrightness, 0.114 + cursorBrightness) + vec3(heightVariation * 0.04); // Brighter under cursor
				
				// Apply lighting to grayscale colors
				vec3 color = heightColor * lighting;
				
				// Apply mask to reveal the animated height effect
				vec3 finalColor = mix(baseColor, color, organicMask);
				
				// Add film grain to the entire scene (not affected by mask)
				finalColor = addFilmGrain(finalColor, uTime);
				
				gl_FragColor = vec4(finalColor, 1.0);
			}
		`
	})

	const geometry = new PlaneGeometry(2, 2)
	const mesh = new Mesh(geometry, maskMaterial)
	mesh.frustumCulled = false
	scene.add(mesh)

	window.addEventListener('mousemove', (event) => {
		mouseTarget.x = event.clientX
		mouseTarget.y = event.clientY
	}, { passive: true })

	let webglPaused = false
	document.addEventListener('visibilitychange', () => {
		webglPaused = document.hidden
	})

	renderWebGL = (time) => {
		if (webglPaused) return
		mouse.lerp(mouseTarget, 0.1)
		maskMaterial.uniforms.uTime.value = time
		maskMaterial.uniforms.uMouse.value.set(mouse.x, mouse.y)
		renderer.render(scene, camera)
	}

	function onWindowResize() {
		const width = window.innerWidth
		const height = window.innerHeight
		renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
		renderer.setSize(width, height, false)
		maskMaterial.uniforms.uResolution.value.set(width, height)
		blurDirty = true
	}

	window.addEventListener('resize', onWindowResize, { passive: true })
}

// One ticker: Lenis + scroll blur + WebGL. lagSmoothing(0) keeps Lenis in lockstep after tab-hide.
gsap.ticker.add((time) => {
	lenis.raf(time * 1000)
	if (blurDirty) {
		blurDirty = false
		handleLineByLineBlur()
	}
	renderWebGL(time)
})
gsap.ticker.lagSmoothing(0)
